import { errorMessage } from "./notificationUtils";

const JFIF_ERROR_MESSAGE =
  "JFIF files are not allowed. Please upload a valid PDF, JPG, JPEG, or PNG file.";

export const HOSPITAL_BILL_MAX_SIZE_BYTES = 5 * 1024 * 1024;

const HOSPITAL_BILL_SIZE_ERROR_MESSAGE =
  "Hospital Bill file size must be less than or equal to 5 MB.";

const getFileExtension = (fileName?: string): string => {
  if (!fileName || !fileName.includes(".")) return "";
  return fileName.split(".").pop()?.toLowerCase()?.trim() || "";
};

/**
 * Only the file currently being selected/changed — not the full Ant Design
 * fileList, which can still contain a previously rejected JFIF.
 */
const getCurrentUploadFile = (e: any): any | null => {
  if (!e || Array.isArray(e)) return null;
  return e?.file?.originFileObj || e?.file || null;
};

export const isHospitalBillDocument = (label?: string | null): boolean =>
  (label || "").toUpperCase().trim() === "HOSPITAL BILL";

export const isJfifFile = (file?: {
  name?: string;
  type?: string;
} | null): boolean => {
  if (!file) return false;
  const extension = getFileExtension(file.name);
  const mimeType = (file.type || "").toLowerCase();
  return (
    extension === "jfif" ||
    mimeType === "image/jfif" ||
    mimeType === "image/pjpeg"
  );
};

export const isFileLargerThan = (
  file?: { size?: number } | null,
  maxBytes: number = HOSPITAL_BILL_MAX_SIZE_BYTES,
): boolean => {
  if (!file || typeof file.size !== "number") return false;
  return file.size > maxBytes;
};

/**
 * Returns true if the current upload file is JFIF (and shows error).
 * Call at the start of handleFileChange; if true, return early.
 */
export const rejectJfifUpload = (e: any): boolean => {
  const currentFile = getCurrentUploadFile(e);
  if (!currentFile || !isJfifFile(currentFile)) return false;
  errorMessage(JFIF_ERROR_MESSAGE);
  return true;
};

/**
 * For Hospital Bill docs only: reject if the current file exceeds 5 MB.
 */
export const rejectOversizedHospitalBillUpload = (
  e: any,
  documentLabel?: string | null,
): boolean => {
  if (!isHospitalBillDocument(documentLabel)) return false;

  const currentFile = getCurrentUploadFile(e);
  if (!isFileLargerThan(currentFile, HOSPITAL_BILL_MAX_SIZE_BYTES)) return false;
  errorMessage(HOSPITAL_BILL_SIZE_ERROR_MESSAGE);
  return true;
};

export const rejectOversizedHospitalBillFile = (
  file?: { size?: number } | null,
): boolean => {
  if (!isFileLargerThan(file, HOSPITAL_BILL_MAX_SIZE_BYTES)) return false;
  errorMessage(HOSPITAL_BILL_SIZE_ERROR_MESSAGE);
  return true;
};
